<template>
  <Layout>
    <div class="form-builder">
      <!-- Header -->
      <div class="builder-header">
        <div class="header-left">
          <button class="btn-back" @click="goBack">
            <i class="bx bx-arrow-back"></i>
          </button>
          <div class="form-title-section">
            <input
              v-model="cardData.name"
              type="text"
              class="form-title-input"
              placeholder="Ficha do Participante"
            />
            <!-- Badge de status removido - ficha sempre ativa -->
          </div>
        </div>
        <div class="header-right">
          <button class="btn-auto-organize" @click="showAutoOrganizeModal = true">
            <i class="bx bx-layer"></i>
            Organizar
          </button>
          <button class="btn-internal-fields" @click="showInternalFieldsModal = true">
            <i class="bx bx-show-alt"></i>
            Preview e Campos Internos
          </button>
          <!-- Preview standalone (desativado - funcionalidade incorporada em "Preview e Campos Internos")
          <button class="btn-preview" @click="showPreviewModal = true">
            <i class="bx bx-show"></i>
            Preview
          </button>
          -->
          <button class="btn-settings" @click="showSettingsModal = true">
            <i class="bx bx-cog"></i>
            Configurações
          </button>
          <button class="btn-save" @click="saveCard" :disabled="saving">
            <i class="bx" :class="saving ? 'bx-loader-alt bx-spin' : 'bx-save'"></i>
            {{ saving ? 'Salvando...' : 'Salvar' }}
          </button>
        </div>
      </div>

      <!-- Steps Bar -->
      <div class="steps-bar">
        <div class="steps-container">
          <div
            class="step-tab"
            :class="{ 'active': !selectedTab }"
            @click="selectTab(null)"
          >
            <span class="step-icon-all"><i class="bx bx-list-ul"></i></span>
            <span class="step-name">Todos</span>
            <span class="step-count">{{ fieldsWithoutTab }}</span>
          </div>

          <draggable
            v-model="localTabs"
            handle=".step-drag-handle"
            animation="200"
            class="steps-draggable"
            @end="onTabsReorder"
          >
            <div
              v-for="(tab, index) in localTabs"
              :key="getTabKey(tab)"
              class="step-tab"
              :class="{ 'active': isTabSelected(tab) }"
              @click="selectTab(tab)"
            >
              <span class="step-drag-handle">
                <i class="bx bx-grid-vertical"></i>
              </span>
              <span class="step-number">{{ index + 1 }}</span>
              <span class="step-name">{{ tab.name }}</span>
              <span
                v-if="tab.source_form_id"
                class="step-source-badge"
                title="Aba de formulário de retorno — só aparece para participantes elegíveis"
              >Retorno</span>
              <span class="step-count">{{ getTabFieldCount(tab) }}</span>
              <button class="step-edit-btn" @click.stop="editTab(tab)">
                <i class="bx bx-pencil"></i>
              </button>
              <button class="step-delete-btn" @click.stop="confirmDeleteTab(tab)">
                <i class="bx bx-x"></i>
              </button>
            </div>
          </draggable>

          <button class="btn-add-step" @click="addTab">
            <i class="bx bx-plus"></i>
            Nova Aba
          </button>
        </div>
      </div>

      <!-- Loading Overlay -->
      <transition name="fade-overlay">
        <div v-if="pageLoading" class="builder-loading-overlay">
          <div class="builder-loading-content">
            <div class="builder-loading-spinner"></div>
            <p>Carregando formulário...</p>
          </div>
        </div>
      </transition>

      <!-- Main Content -->
      <div class="builder-content">
        <!-- Left Panel: Field Palette -->
        <div class="panel panel-left">
          <FieldPalette
            :available-fields="availableFields"
            :loading="isLoading"
            :tabs="tabs"
            :settings="rsvpSettings || {}"
            @add-field="handleAddField"
          />
        </div>

        <!-- Center Panel: Card Canvas -->
        <div class="panel panel-center">
          <div class="canvas-header">
            <h5 v-if="selectedTab">
              <i class="bx bx-layer"></i>
              {{ selectedTab.name }}
            </h5>
            <h5 v-else>
              <i class="bx bx-list-ul"></i>
              Todos os Campos
            </h5>
            <span class="field-count">{{ filteredFields.length }} campos</span>
          </div>

          <div class="canvas-scroll">
            <CardCanvas
              :fields="filteredFields"
              :selected-field="selectedField"
              @add-field="handleAddField"
              @edit-field="handleEditField"
              @remove-field="handleDeleteField"
              @reorder-fields="handleFieldReorder"
              @select-field="handleSelectField"
            />
          </div>
        </div>

        <!-- Right Panel: Field Editor -->
        <div class="panel panel-right" :class="{ 'has-selection': selectedField }">
          <div v-if="!selectedField" class="no-selection">
            <div class="no-selection-icon">
              <i class="bx bx-pointer"></i>
            </div>
            <h5>Nenhum campo selecionado</h5>
            <p>Clique em um campo para editar suas propriedades</p>
          </div>

          <FieldEditor
            v-else
            :field="selectedField"
            :groups="groups"
            :all-fields="allCardFields"
            @change="handleFieldChange"
          />
        </div>
      </div>

      <!-- Settings Modal -->
      <b-modal
        v-model="showSettingsModal"
        title="Configurações da Ficha"
        size="lg"
        ok-title="Salvar"
        cancel-title="Cancelar"
        @ok="saveSettings"
        centered
      >
        <div class="settings-form">
          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label>Nome da Ficha *</label>
                <input
                  v-model="cardData.name"
                  type="text"
                  class="form-control"
                  placeholder="Digite o nome da ficha"
                />
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label>Descrição</label>
                <textarea
                  v-model="cardData.description"
                  class="form-control"
                  rows="3"
                  placeholder="Descrição da ficha (opcional)"
                ></textarea>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label>Grupo</label>
                <select v-model="cardData.group_id" class="form-control">
                  <option :value="null">Global (todos os grupos)</option>
                  <option
                    v-for="group in groups"
                    :key="group.id"
                    :value="group.id"
                  >{{ group.name }}</option>
                </select>
                <small class="text-muted">
                  Ficha global é usada como fallback. Fichas com grupo específico têm prioridade para participantes daquele grupo.
                </small>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <div class="form-group mb-0">
                <label class="d-flex align-items-center">
                  <input
                    v-model="cardData.is_active"
                    type="checkbox"
                    class="mr-2"
                  />
                  Ficha ativa
                </label>
                <small class="text-muted">
                  Quando ativa, esta ficha será exibida na visualização dos participantes
                </small>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group mb-0">
                <label class="d-flex align-items-center">
                  <input
                    v-model="cardData.is_default"
                    type="checkbox"
                    class="mr-2"
                  />
                  Ficha padrão
                </label>
                <small class="text-muted">
                  Marcada como padrão para grupos sem ficha específica. Só uma ficha global pode ser padrão.
                </small>
              </div>
            </div>
          </div>
        </div>
      </b-modal>

      <!-- Tab Modal -->
      <b-modal
        v-model="showTabModal"
        :title="editingTab && editingTab.id ? 'Editar Aba' : 'Nova Aba'"
        ok-title="Salvar"
        cancel-title="Cancelar"
        @ok="saveTab"
        centered
      >
        <div class="form-group mb-0">
          <label>Nome da Aba *</label>
          <input
            v-model="editingTab.name"
            type="text"
            class="form-control"
            placeholder="Ex: Dados Pessoais"
          />
        </div>
      </b-modal>

      <!-- Preview Modal -->
      <CardPreviewModal
        :visible="showPreviewModal"
        :tabs="tabs"
        :card-name="cardData.name || 'Ficha do Participante'"
        @close="showPreviewModal = false"
      />

      <!-- Auto Organize Modal -->
      <AutoOrganizeModal
        ref="autoOrganizeModal"
        :visible="showAutoOrganizeModal"
        :tabs="tabs"
        @close="showAutoOrganizeModal = false"
        @organize="handleAutoOrganize"
      />

      <!-- Internal Fields Modal -->
      <InternalFieldsModal
        :visible="showInternalFieldsModal"
        :tabs="tabs"
        :card-name="cardData.name || 'Ficha do Participante'"
        @close="showInternalFieldsModal = false"
        @create-field="handleInternalCreateField"
        @update-field="handleInternalUpdateField"
        @delete-field="handleInternalDeleteField"
        @create-tab="handleInternalCreateTab"
        @reorder-tabs="handleInternalReorderTabs"
        @reorder-fields="handleInternalReorderFields"
      />
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import draggable from 'vuedraggable'
import FieldPalette from './components/FieldPalette.vue'
import CardCanvas from './components/CardCanvas.vue'
import FieldEditor from './components/FieldEditor.vue'
import CardPreviewModal from './components/CardPreviewModal.vue'
import AutoOrganizeModal from './components/AutoOrganizeModal.vue'
import InternalFieldsModal from './components/InternalFieldsModal.vue'
import { mapActions, mapState, mapGetters } from 'vuex'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import http from '@/http'

export default {
  name: 'ParticipantCardBuilder',
  components: {
    Layout,
    draggable,
    FieldPalette,
    CardCanvas,
    FieldEditor,
    CardPreviewModal,
    AutoOrganizeModal,
    InternalFieldsModal
  },
  data: function () {
    return {
      cardData: {
        id: null,
        name: '',
        description: '',
        group_id: null,
        is_active: true,
        is_default: false
      },
      selectedField: null,
      selectedTab: null,
      saving: false,
      showSettingsModal: false,
      showTabModal: false,
      showPreviewModal: false,
      showAutoOrganizeModal: false,
      showInternalFieldsModal: false,
      editingTab: {
        name: ''
      },
      pageLoading: true,
      groups: [],
      localTabs: []
    }
  },
  watch: {
    tabs: {
      handler: function (newTabs) {
        this.localTabs = newTabs ? [...newTabs] : []
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    ...mapState('participantCardBuilder', ['availableFields', 'card', 'tabs']),
    ...mapGetters('participantCardBuilder', ['isDirty', 'isLoading']),
    ...mapState('settingRSVP', { rsvpSettings: 'settingRSVP' }),
    isEditing: function () {
      return !!this.$route.params.id
    },
    filteredFields: function () {
      if (!this.selectedTab) {
        return []
      }
      var self = this
      var tab = this.tabs.find(function (t) {
        var tabId = t.id || t._tempId
        var selectedId = self.selectedTab.id || self.selectedTab._tempId
        return tabId === selectedId
      })
      if (tab && tab.fields) {
        return tab.fields
      }
      return []
    },
    fieldsWithoutTab: function () {
      return 0
    },
    allCardFields: function () {
      if (!this.tabs) return []
      var fields = []
      this.tabs.forEach(function (tab) {
        if (tab.fields) {
          fields = fields.concat(tab.fields)
        }
      })
      return fields
    }
  },
  mounted: function () {
    document.body.classList.add('form-builder-page')

    this.loadGroups()
    this.loadAvailableFields()
    this.loadRSVPSettings()

    // Sempre carrega a ficha (é obrigatório ter um ID na rota)
    if (this.$route.params.id) {
      this.loadCard(this.$route.params.id)
    } else {
      // Se não tiver ID, redireciona para a página de índice que vai criar/carregar a ficha global
      this.$router.replace({ name: 'participant-cards' })
    }
  },
  beforeDestroy: function () {
    document.body.classList.remove('form-builder-page')
    this.resetBuilder()
  },
  methods: {
    ...mapActions('participantCardBuilder', [
      'fetchCard',
      'fetchAvailableFields',
      'createCard',
      'updateCard',
      'createTab',
      'updateTab',
      'deleteTab',
      'createField',
      'updateField',
      'deleteField',
      'reorderFields',
      'reorderTabs',
      'autoOrganize',
      'resetBuilder'
    ]),

    ...mapActions('settingRSVP', { GetRSVP: 'GetRSVP' }),

    loadRSVPSettings: function () {
      this.GetRSVP().catch(function (err) {
        console.error('Error loading RSVP settings:', err)
      })
    },

    loadGroups: function () {
      var self = this
      http.get('/admin/groups', { params: { all: true } })
        .then(function (response) {
          self.groups = response.data.data || response.data || []
        })
        .catch(function (error) {
          console.error('Error loading groups:', error)
        })
    },

    loadAvailableFields: function () {
      this.fetchAvailableFields().catch(function () {
        errorMessage('Erro ao carregar campos disponíveis')
      })
    },

    loadCard: function (id) {
      var self = this
      this.pageLoading = true
      this.fetchCard(id)
        .then(function () {
          if (self.card) {
            self.cardData = Object.assign({}, self.card)
            if (self.tabs.length > 0 && !self.selectedTab) {
              self.selectedTab = self.tabs[0]
            }
          }
        })
        .catch(function () {
          errorMessage('Erro ao carregar ficha')
        })
        .finally(function () {
          self.pageLoading = false
        })
    },

    saveCard: function () {
      var self = this
      if (!this.cardData.name) {
        errorMessage('Por favor, preencha o nome da ficha')
        return
      }

      this.saving = true

      if (this.isEditing) {
        this.updateCard(this.cardData)
          .then(function () {
            successMessage('Ficha atualizada com sucesso')
          })
          .catch(function () {
            errorMessage('Erro ao salvar ficha')
          })
          .finally(function () {
            self.saving = false
          })
      } else {
        this.createCard(this.cardData)
          .then(function (response) {
            self.cardData.id = response.id
            self.$router.replace({ name: 'participant-card-builder', params: { id: response.id } })
            successMessage('Ficha criada com sucesso')
          })
          .catch(function () {
            errorMessage('Erro ao salvar ficha')
          })
          .finally(function () {
            self.saving = false
          })
      }
    },

    saveSettings: function () {
      var self = this
      if (!this.cardData.name) {
        errorMessage('Por favor, preencha o nome da ficha')
        return
      }

      if (!this.isEditing) {
        errorMessage('Salve a ficha primeiro antes de editar as configurações')
        return
      }

      var payload = {
        name: this.cardData.name,
        description: this.cardData.description,
        is_active: this.cardData.is_active,
        is_default: this.cardData.is_default,
        group_id: this.cardData.group_id
      }

      http.put('/admin/participant-cards/' + this.cardData.id, payload)
        .then(function (response) {
          // Apenas atualiza os dados básicos, sem recarregar tabs
          self.cardData = Object.assign({}, self.cardData, response.data)
          successMessage('Configurações salvas com sucesso')
        })
        .catch(function (error) {
          var msg = error.response && error.response.data && error.response.data.message
          errorMessage(msg || 'Erro ao salvar configurações')
        })
    },

    addTab: function () {
      if (!this.cardData.id) {
        errorMessage('Salve a ficha antes de adicionar abas')
        return
      }
      this.editingTab = {
        name: '',
        type: 'accordion',
        is_visible: true,
        is_expanded_default: false
      }
      this.showTabModal = true
    },

    editTab: function (tab) {
      this.editingTab = Object.assign({}, tab)
      this.showTabModal = true
    },

    saveTab: function () {
      var self = this
      if (!this.editingTab.name) {
        errorMessage('Digite um nome para a aba')
        return
      }

      if (this.editingTab.id) {
        this.updateTab({ tabId: this.editingTab.id, data: this.editingTab })
          .then(function () {
            successMessage('Aba atualizada')
          })
          .catch(function (error) {
            var msg = error.response && error.response.data && error.response.data.message
            errorMessage(msg || 'Erro ao salvar aba')
          })
      } else {
        this.editingTab.participant_card_id = this.cardData.id
        this.createTab(this.editingTab)
          .then(function (newTab) {
            successMessage('Aba criada')
            if (newTab) {
              self.selectedTab = newTab
            }
          })
          .catch(function (error) {
            var msg = error.response && error.response.data && error.response.data.message
            errorMessage(msg || 'Erro ao salvar aba')
          })
      }
    },

    confirmDeleteTab: function (tab) {
      var self = this
      this.$bvModal.msgBoxConfirm(
        'Tem certeza que deseja deletar a aba "' + tab.name + '"?',
        {
          title: 'Deletar Aba',
          okTitle: 'Sim, deletar',
          okVariant: 'danger',
          cancelTitle: 'Cancelar',
          centered: true
        }
      ).then(function (confirmed) {
        if (confirmed) {
          self.deleteTab(tab.id)
            .then(function () {
              if (self.selectedTab && self.selectedTab.id === tab.id) {
                self.selectedTab = self.tabs.length > 0 ? self.tabs[0] : null
              }
              successMessage('Aba deletada com sucesso')
            })
            .catch(function (error) {
              var msg = error.response && error.response.data && error.response.data.message
              errorMessage(msg || 'Erro ao deletar aba')
            })
        }
      })
    },

    selectTab: function (tab) {
      this.selectedTab = tab
      this.selectedField = null
    },

    isTabSelected: function (tab) {
      if (!this.selectedTab) return false
      if (this.selectedTab.id && tab.id) {
        return String(this.selectedTab.id) === String(tab.id)
      }
      if (this.selectedTab._tempId && tab._tempId) {
        return String(this.selectedTab._tempId) === String(tab._tempId)
      }
      return false
    },

    onTabsReorder: function () {
      var tabsOrder = this.localTabs.map(function (tab, index) {
        return { id: tab.id, order: index + 1 }
      })
      this.reorderTabs(tabsOrder).catch(function (error) {
        var msg = error.response && error.response.data && error.response.data.message
        errorMessage(msg || 'Erro ao reordenar abas')
      })
    },

    handleAddField: function (fieldData) {
      var self = this
      if (!this.selectedTab) {
        errorMessage('Selecione uma aba primeiro')
        return
      }

      if (!this.cardData.id) {
        errorMessage('Salve a ficha antes de adicionar campos')
        return
      }

      if (!this.selectedTab.id) {
        errorMessage('Salve a aba antes de adicionar campos')
        return
      }

      var fieldPayload = {
        field_source: fieldData.field_source,
        label: fieldData.label,
        icon: fieldData.icon,
        layout: { cols: 12 },
        required: fieldData.required || false,
        visible: true
      }

      // Standard field
      if (fieldData.field_source === 'standard') {
        fieldPayload.standard_field_name = fieldData.standard_field_name
      }

      // Form field
      if (fieldData.field_source === 'form_field') {
        fieldPayload.form_field_id = fieldData.form_field_id
      }

      // Internal/custom field
      if (fieldData.field_source === 'internal') {
        var fieldLabel = fieldData.label || 'Campo'
        var fieldName = fieldData.name || 'campo_' + Date.now()
        fieldPayload.field_config = {
          field_type: fieldData.field_type,
          label: fieldLabel,
          name: fieldName,
          placeholder: fieldData.placeholder,
          help_text: fieldData.help_text,
          options: fieldData.options,
          is_internal: true
        }
      }

      // Registration control field
      if (fieldData.field_source === 'registration_control') {
        fieldPayload.is_readonly = true
        fieldPayload.field_config = {
          source: 'registration_control',
          rc_field: fieldData.rc_field,
          field_type: fieldData.rc_type || 'text',
          label: fieldData.label,
          name: 'rc_' + fieldData.rc_field,
          mask: fieldData.rc_mask || null,
          options: fieldData.rc_options || null
        }
      }

      this.createField({
        tabId: this.selectedTab.id,
        data: fieldPayload
      })
        .then(function (newField) {
          self.selectedField = newField
          successMessage('Campo adicionado com sucesso')
        })
        .catch(function (error) {
          var msg = error.response && error.response.data && error.response.data.message
          errorMessage(msg || 'Erro ao adicionar campo')
        })
    },

    handleEditField: function (field) {
      this.selectedField = field
    },

    handleDeleteField: function (fieldId) {
      var self = this
      if (!this.selectedTab || !this.selectedTab.id) return

      this.deleteField({
        tabId: this.selectedTab.id,
        fieldId: fieldId
      })
        .then(function () {
          if (self.selectedField && self.selectedField.id === fieldId) {
            self.selectedField = null
          }
          successMessage('Campo removido com sucesso')
        })
        .catch(function (error) {
          var msg = error.response && error.response.data && error.response.data.message
          errorMessage(msg || 'Erro ao remover campo')
        })
    },

    handleFieldReorder: function (fields) {
      if (!this.selectedTab || !this.selectedTab.id) return

      this.reorderFields({
        tabId: this.selectedTab.id,
        fields: fields
      }).catch(function (error) {
        var msg = error.response && error.response.data && error.response.data.message
        errorMessage(msg || 'Erro ao reordenar campos')
      })
    },

    handleSelectField: function (field) {
      this.selectedField = field
    },

    handleFieldChange: function (field) {
      var self = this
      if (!this.selectedField || !this.selectedTab) return

      if (!this.selectedField.id || !this.selectedTab.id) {
        this.selectedField = field
        return
      }

      // Para campos internos, atualizar o field_config com label e name
      var fieldData = Object.assign({}, field)
      if (field.field_source === 'internal') {
        fieldData.field_config = Object.assign({}, field.field_config || {}, {
          label: field.label || field.name || 'Campo',
          name: field.name,
          placeholder: field.placeholder,
          help_text: field.help_text
        })
      }

      this.updateField({
        tabId: this.selectedTab.id,
        fieldId: this.selectedField.id,
        data: fieldData
      })
        .then(function (updatedField) {
          self.selectedField = updatedField
        })
        .catch(function (error) {
          var msg = error.response && error.response.data && error.response.data.message
          errorMessage(msg || 'Erro ao atualizar campo')
        })
    },

    // ---- Internal Fields Modal handlers ----
    handleInternalCreateField: function (payload) {
      var tab = this.tabs.find(function (t) { return t.id === payload.tabId })
      var maxOrder = 0
      if (tab && tab.fields) {
        maxOrder = tab.fields.reduce(function (max, f) { return Math.max(max, f.order || 0) }, 0)
      }
      var data = Object.assign({}, payload.data, { order: maxOrder + 1 })
      this.createField({ tabId: payload.tabId, data: data })
        .then(function () {
          if (payload.callback) payload.callback(true)
          successMessage('Campo adicionado')
        })
        .catch(function (error) {
          if (payload.callback) payload.callback(false)
          var msg = error.response && error.response.data && error.response.data.message
          errorMessage(msg || 'Erro ao adicionar campo')
        })
    },
    handleInternalUpdateField: function (payload) {
      this.updateField({ tabId: payload.tabId, fieldId: payload.fieldId, data: payload.data })
        .then(function () {
          if (payload.callback) payload.callback(true)
          successMessage('Campo atualizado')
        })
        .catch(function (error) {
          if (payload.callback) payload.callback(false)
          var msg = error.response && error.response.data && error.response.data.message
          errorMessage(msg || 'Erro ao atualizar campo')
        })
    },
    handleInternalDeleteField: function (payload) {
      this.deleteField({ tabId: payload.tabId, fieldId: payload.fieldId })
        .then(function () {
          successMessage('Campo removido')
        })
        .catch(function (error) {
          var msg = error.response && error.response.data && error.response.data.message
          errorMessage(msg || 'Erro ao remover campo')
        })
    },
    handleInternalCreateTab: function (payload) {
      var maxOrder = this.tabs.reduce(function (max, t) { return Math.max(max, t.order || 0) }, 0)
      this.createTab({ participant_card_id: this.cardData.id, name: payload.name, type: 'accordion', order: maxOrder + 1 })
        .then(function () {
          if (payload.callback) payload.callback(true)
          successMessage('Aba criada')
        })
        .catch(function (error) {
          if (payload.callback) payload.callback(false)
          var msg = error.response && error.response.data && error.response.data.message
          errorMessage(msg || 'Erro ao criar aba')
        })
    },
    handleInternalReorderFields: function (payload) {
      this.reorderFields({
        tabId: payload.tabId,
        fields: payload.fields
      }).catch(function (error) {
        var msg = error.response && error.response.data && error.response.data.message
        errorMessage(msg || 'Erro ao reordenar campos')
      })
    },
    handleInternalReorderTabs: function (payload) {
      this.reorderTabs(payload.tabs)
        .then(function () {
          if (payload.callback) payload.callback(true)
          successMessage('Abas reordenadas')
        })
        .catch(function (error) {
          if (payload.callback) payload.callback(false)
          var msg = error.response && error.response.data && error.response.data.message
          errorMessage(msg || 'Erro ao reordenar abas')
        })
    },

    handleAutoOrganize: function (data) {
      var self = this
      var modal = this.$refs.autoOrganizeModal
      if (modal) modal.setLoading(true)

      this.autoOrganize(data)
        .then(function (card) {
          if (modal) modal.setLoading(false)
          self.showAutoOrganizeModal = false
          // Atualizar dados locais
          if (card) {
            self.cardData = Object.assign({}, card)
            if (card.tabs && card.tabs.length > 0) {
              self.selectedTab = card.tabs[0]
            } else {
              self.selectedTab = null
            }
            self.selectedField = null
          }
          successMessage('Ficha organizada com sucesso!')
        })
        .catch(function (error) {
          if (modal) modal.setLoading(false)
          var msg = error.response && error.response.data && error.response.data.message
          errorMessage(msg || 'Erro ao organizar ficha automaticamente')
        })
    },

    goBack: function () {
      this.$router.push({ name: 'participants' })
    },

    getTabKey: function (tab) {
      return tab.id || tab._tempId
    },

    getTabFieldCount: function (tab) {
      if (tab && tab.fields) {
        return tab.fields.length
      }
      return 0
    }
  }
}
</script>

<style scoped>
.form-builder {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f6fa;
  overflow: hidden;
  position: relative;
}

/* Header */
.builder-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.btn-back {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 8px;
  cursor: pointer;
  color: #495057;
  transition: all 0.15s;
}

.btn-back:hover {
  border-color: #556ee6;
  color: #556ee6;
}

.form-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.form-title-input {
  border: none;
  background: transparent;
  font-size: 18px;
  font-weight: 600;
  color: #344054;
  padding: 4px 0;
  width: 300px;
}

.form-title-input:focus {
  outline: none;
  border-bottom: 2px solid #556ee6;
}

.form-status {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

.form-status.active {
  background: #d1fae5;
  color: #059669;
}

.form-status.inactive {
  background: #fee2e2;
  color: #dc2626;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-settings {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  color: #495057;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-settings:hover {
  border-color: #556ee6;
  color: #556ee6;
}

.btn-internal-fields {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #10b981;
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  color: #10b981;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-internal-fields:hover {
  background: #10b981;
  color: #fff;
}

.btn-preview {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #17a2b8;
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  color: #17a2b8;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-preview:hover {
  background: #17a2b8;
  color: #fff;
}

.btn-auto-organize {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #7c3aed;
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  color: #7c3aed;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-auto-organize:hover {
  background: #7c3aed;
  color: #fff;
}

.btn-save {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  border: none;
  background: #556ee6;
  color: #fff;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-save:hover:not(:disabled) {
  background: #485ec4;
}

.btn-save:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Steps Bar */
.steps-bar {
  background: #fff;
  border-bottom: 1px solid #e9ecef;
  padding: 0 20px;
  flex-shrink: 0;
}

.steps-container {
  display: flex;
  align-items: center;
  gap: 4px;
  overflow-x: auto;
  padding: 8px 0;
}

.steps-draggable {
  display: flex;
  gap: 4px;
}

.step-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
}

.step-tab:hover {
  border-color: #d0d5dd;
  background: #f0f2f5;
}

.step-tab.active {
  background: #556ee6;
  border-color: #556ee6;
  color: #fff;
}

.step-drag-handle {
  cursor: grab;
  color: #adb5bd;
  display: flex;
}

.step-tab.active .step-drag-handle {
  color: rgba(255, 255, 255, 0.7);
}

.step-icon-all {
  display: flex;
  align-items: center;
  color: #556ee6;
}

.step-tab.active .step-icon-all {
  color: #fff;
}

.step-number {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e9ecef;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 600;
  color: #495057;
}

.step-tab.active .step-number {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.step-name {
  font-size: 13px;
  font-weight: 500;
}

.step-source-badge {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 2px 6px;
  background: #fef3c7;
  color: #92400e;
  border-radius: 4px;
  margin-right: 4px;
}

.step-tab.active .step-source-badge {
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
}

.step-count {
  font-size: 11px;
  padding: 2px 6px;
  background: #e9ecef;
  border-radius: 10px;
  color: #6c757d;
}

.step-tab.active .step-count {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.step-edit-btn,
.step-delete-btn {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0;
  transition: all 0.15s;
  color: inherit;
}

.step-tab:hover .step-edit-btn,
.step-tab:hover .step-delete-btn {
  opacity: 0.7;
}

.step-edit-btn:hover,
.step-delete-btn:hover {
  opacity: 1 !important;
}

.btn-add-step {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border: 1px dashed #adb5bd;
  background: transparent;
  border-radius: 8px;
  font-size: 13px;
  color: #6c757d;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
}

.btn-add-step:hover {
  border-color: #556ee6;
  color: #556ee6;
}

/* Main Content */
.builder-content {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-left {
  width: 320px;
  border-right: 1px solid #e9ecef;
  background: #fff;
  flex-shrink: 0;
}

.panel-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #f5f6fa;
}

.canvas-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
}

.canvas-header h5 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 8px;
}

.canvas-header h5 i {
  color: #556ee6;
}

.canvas-header .field-count {
  font-size: 12px;
  color: #6c757d;
}

.canvas-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  min-height: 0;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}

.canvas-scroll::-webkit-scrollbar {
  width: 6px;
}

.canvas-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.canvas-scroll::-webkit-scrollbar-thumb {
  background-color: #d0d5dd;
  border-radius: 3px;
}

.canvas-scroll::-webkit-scrollbar-thumb:hover {
  background-color: #b0b5bd;
}

.panel-right {
  width: 0;
  background: #fff;
  border-left: 1px solid #e9ecef;
  transition: width 0.2s ease;
  overflow: hidden;
  flex-shrink: 0;
}

.panel-right.has-selection {
  width: 360px;
}

.no-selection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px;
  text-align: center;
}

.no-selection-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f4ff;
  border-radius: 50%;
  margin-bottom: 16px;
}

.no-selection-icon i {
  font-size: 28px;
  color: #556ee6;
}

.no-selection h5 {
  font-size: 15px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

.no-selection p {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
}

/* Settings Form */
.settings-form .form-group {
  margin-bottom: 16px;
}

.settings-form label {
  font-size: 13px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 6px;
  display: block;
}

/* Responsive */
@media (max-width: 1400px) {
  .panel-left {
    width: 280px;
  }
  .panel-right.has-selection {
    width: 320px;
  }
}

@media (max-width: 1200px) {
  .panel-right.has-selection {
    width: 300px;
  }
}

/* Loading Overlay */
.builder-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(245, 246, 250, 0.82);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}
.builder-loading-content {
  text-align: center;
  background: #fff;
  padding: 32px 48px;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
}
.builder-loading-content p {
  margin: 16px 0 0;
  font-size: 15px;
  font-weight: 500;
  color: #495057;
}
.builder-loading-spinner {
  width: 36px;
  height: 36px;
  margin: 0 auto;
  border: 3px solid #e9ecef;
  border-top-color: #556ee6;
  border-radius: 50%;
  animation: builder-spin 0.8s linear infinite;
}
@keyframes builder-spin {
  to { transform: rotate(360deg); }
}
.fade-overlay-enter-active {
  transition: opacity 0.15s ease;
}
.fade-overlay-leave-active {
  transition: opacity 0.3s ease;
}
.fade-overlay-enter,
.fade-overlay-leave-to {
  opacity: 0;
}
</style>

<style>
/* Global style to hide footer on form builder page */
body.form-builder-page {
  overflow: hidden !important;
  height: 100vh !important;
}

body.form-builder-page footer.footer {
  display: none !important;
}

body.form-builder-page .main-content {
  padding: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  margin-right: 0 !important;
  overflow: hidden !important;
  height: 100vh !important;
  display: flex !important;
  flex-direction: column !important;
}

body.form-builder-page .page-content {
  padding: 0 !important;
  margin: 0 !important;
  margin-top: 70px !important;
  overflow: hidden !important;
  min-height: 0 !important;
  height: calc(100vh - 70px) !important;
  max-height: calc(100vh - 70px) !important;
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
}

body.form-builder-page .page-content > .container-fluid {
  padding: 0 !important;
  margin: 0 !important;
  height: 100% !important;
  min-height: 0 !important;
  max-width: 100% !important;
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}
</style>
